import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { type LogGroups, NO_LOG_GROUP } from "@/routes/environments/log-streams/log-groups";
import { formatError } from "@/utils/errors";
import { formatAllLogsForCopy } from "@/utils/log-copy-format";
import { type FC, useCallback, useState } from "react";

type CopyAllLogsButtonProps = {
    logGroups?: LogGroups;
    trackingId?: string;
    variant?: "ghost" | "primary" | "secondary" | "destructive";
    size?: "xs" | "sm" | "md" | "lg";
    className?: string;
};

export const CopyAllLogsButton: FC<CopyAllLogsButtonProps> = ({
    logGroups,
    trackingId = "copy-all-logs",
    variant = "ghost",
    size = "sm",
    className,
}) => {
    const { toast } = useToast();
    const [isCopying, setIsCopying] = useState(false);
    const [copied, setCopied] = useState(false);

    const isLegacyRunner =
        logGroups && Object.keys(logGroups).length === 1 && logGroups[NO_LOG_GROUP]?.lines?.length > 0;

    const handleCopyAllLogs = useCallback(async () => {
        if (!logGroups) return;

        setIsCopying(true);
        setCopied(false);

        try {
            const textToCopy = formatAllLogsForCopy(logGroups, !!isLegacyRunner);
            await navigator.clipboard.writeText(textToCopy);

            setCopied(true);
            toast({ title: "Copied all logs to clipboard" });
        } catch (error) {
            toast({
                title: "Failed to copy logs",
                description: formatError(error),
            });
        } finally {
            setIsCopying(false);
            setTimeout(() => setCopied(false), 2000);
        }
    }, [logGroups, isLegacyRunner, toast]);

    return (
        <Tooltip content="Copy all logs to clipboard" usePortal={true}>
            <Button
                variant={variant}
                size={size}
                className={cn(
                    "border-0",
                    {
                        "disabled:text-content-success": copied,
                    },
                    className,
                )}
                LeadingIcon={copied ? IconCheck : IconCopy}
                disabled={isCopying || copied || !logGroups}
                onClick={handleCopyAllLogs}
                aria-label="Copy all logs"
                data-tracking-id={trackingId}
            >
                <span>{isCopying ? "Copying..." : copied ? "Copied!" : "Copy all logs"}</span>
            </Button>
        </Tooltip>
    );
};
